import React from "react";
import {Button, Space, Switch, Tag} from "antd";
import {CheckCircleFilled, CloseOutlined, DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {TICKET_PRIORITY_COLORS} from "constants/ticket";
import {getAutoAssignRoleOptions, getTopicOptions} from "./ticketTypeOptions";

export const TicketTypesTable = ({
    rows,
    total,
    loading,
    options,
    filters,
    togglingId,
    onPageChange,
    onEdit,
    onDelete,
    onToggleActive
}) => {
    const {t} = useTranslation(["tickets"]);

    const getTopicName = record =>
        getTopicOptions(options, record.category).find(topic => topic.key === record.topic)?.name ||
        record.topic;

    const roleColumns = getAutoAssignRoleOptions(options).map(role => ({
        title: role.name,
        key: `role-${role.key}`,
        align: "center",
        render: (_, record) =>
            record.autoAssignRole?.includes(role.key) ? (
                <CheckCircleFilled style={{color: "#389e0d", fontSize: 20}} />
            ) : (
                <CloseOutlined style={{color: "#d9d9d9", fontSize: 14}} />
            ),
        onCell: () => ({"data-label": role.name})
    }));

    const columns = [
        {
            title: t("ticketTypes.columns.name", {ns: "tickets"}),
            dataIndex: "name",
            key: "name",
            onCell: () => ({"data-label": t("ticketTypes.columns.name", {ns: "tickets"})})
        },
        {
            title: t("ticketTypes.columns.category", {ns: "tickets"}),
            dataIndex: "category",
            key: "category",
            render: category => <Tag>{t(`categories.${category}`, {ns: "tickets"})}</Tag>,
            onCell: () => ({"data-label": t("ticketTypes.columns.category", {ns: "tickets"})})
        },
        {
            title: t("ticketTypes.columns.topic", {ns: "tickets"}),
            key: "topic",
            render: (_, record) => getTopicName(record),
            onCell: () => ({"data-label": t("ticketTypes.columns.topic", {ns: "tickets"})})
        },
        {
            title: t("ticketTypes.columns.priority", {ns: "tickets"}),
            dataIndex: "priority",
            key: "priority",
            render: priority => (
                <Tag color={TICKET_PRIORITY_COLORS[priority]}>
                    {t(`priorities.${priority}`, {ns: "tickets"})}
                </Tag>
            ),
            onCell: () => ({"data-label": t("ticketTypes.columns.priority", {ns: "tickets"})})
        },
        {
            title: t("ticketTypes.columns.photoRequired", {ns: "tickets"}),
            dataIndex: "isPhotoRequired",
            key: "isPhotoRequired",
            render: isPhotoRequired =>
                isPhotoRequired
                    ? t("ticketTypes.yes", {ns: "tickets"})
                    : t("ticketTypes.no", {ns: "tickets"}),
            onCell: () => ({"data-label": t("ticketTypes.columns.photoRequired", {ns: "tickets"})})
        },
        ...roleColumns,
        {
            title: t("ticketTypes.columns.isActive", {ns: "tickets"}),
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive, record) => (
                <Switch
                    checked={isActive}
                    loading={togglingId === record._id}
                    onChange={checked => onToggleActive(record, checked)}
                />
            ),
            onCell: () => ({"data-label": t("ticketTypes.columns.isActive", {ns: "tickets"})})
        },
        {
            title: t("ticketTypes.columns.action", {ns: "tickets"}),
            key: "action",
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => onEdit(record)}
                    >
                        {t("ticketTypes.edit", {ns: "tickets"})}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => onDelete(record)}
                    >
                        {t("ticketTypes.delete", {ns: "tickets"})}
                    </Button>
                </Space>
            ),
            onCell: () => ({"data-label": t("ticketTypes.columns.action", {ns: "tickets"})})
        }
    ];

    return (
        <ResponsiveDataTable
            size="small"
            columns={columns}
            dataSource={rows}
            rowKey="_id"
            loading={loading}
            scroll={{x: "max-content"}}
            pagination={{
                current: Math.floor(filters.skip / filters.limit) + 1,
                pageSize: filters.limit,
                total,
                showTotal: count => t("ticketTypes.total", {ns: "tickets", total: count}),
                onChange: onPageChange
            }}
        />
    );
};
